import type { jsPDF } from 'jspdf';
import type { CardBackLogoType, CardBackStyle } from '../types';

/**
 * Card back renderer for jsPDF.
 * Renders card backs to canvas images with proper rounded corners,
 * then adds them to the PDF. This approach:
 * 1. Uses canvas 2D roundRect for proper rounded corners
 * 2. Caches rendered card backs by color/logo/style combo
 * 3. SVG logos are rendered once and cached as data URLs
 */

// Cache key: "logoType-color" -> data URL
const logoCache = new Map<string, string>();

// Cache for fetched SVG content
const svgContentCache = new Map<string, string>();

/**
 * Map of logo types to their external SVG file paths.
 * Logos not in this map use inline SVG (arcane-runes only).
 */
const externalLogoFiles: Partial<Record<CardBackLogoType, string>> = {
  'd20': '/logos/d20.svg',
  'crossed-swords': '/logos/crossed-swords.svg',
  'dragon': '/logos/dragon.svg',
  'spellbook': '/logos/spellbook.svg',
  'wizard-tower': '/logos/tower.svg',
  'crystal-ball': '/logos/crystal-ball.svg',
  'potion-bottle': '/logos/potion.svg',
  'shield-crest': '/logos/shield.svg',
  'skull-bones': '/logos/skull.svg',
};

/**
 * Fetches an external SVG file and caches the content.
 */
async function fetchSvgContent(path: string): Promise<string> {
  const cached = svgContentCache.get(path);
  if (cached) return cached;
  
  const response = await fetch(path);
  const text = await response.text();
  svgContentCache.set(path, text);
  return text;
}

/**
 * Recolors an SVG by replacing fill and stroke colors.
 * Handles various SVG color patterns including style blocks.
 */
function recolorSvg(svgContent: string, color: string): string {
  let result = svgContent;
  
  // Replace fill="#000000" and fill="#434343" patterns (common in the SVGs)
  result = result.replace(/fill="#[0-9a-fA-F]{3,6}"/g, `fill="${color}"`);
  result = result.replace(/fill:#[0-9a-fA-F]{3,6}/g, `fill:${color}`);
  
  // Replace stroke colors
  result = result.replace(/stroke="#[0-9a-fA-F]{3,6}"/g, `stroke="${color}"`);
  result = result.replace(/stroke:#[0-9a-fA-F]{3,6}/g, `stroke:${color}`);
  
  // Handle CSS style blocks including CDATA sections (like in skull.svg)
  result = result.replace(/\.st(\d+)\s*\{\s*fill:\s*#[0-9a-fA-F]{3,6}\s*;\s*\}/g, `.st$1{fill:${color};}`);
  
  // Handle fill="none" - keep it as none
  result = result.replace(/fill="none"/g, 'FILL_NONE_PLACEHOLDER');
  result = result.replace(/fill="${color}"/g, `fill="${color}"`);
  result = result.replace(/FILL_NONE_PLACEHOLDER/g, 'fill="none"');
  
  return result;
}

/**
 * Generates SVG markup for inline logos (arcane-runes only).
 */
function getInlineLogoSvg(type: 'arcane-runes', color: string): string {
  const svgStart = `<svg xmlns="http://www.w3.org/2000/svg" width="192" height="192" viewBox="0 0 100 100" fill="none">`;
  const svgEnd = `</svg>`;

  const logos: Record<'arcane-runes', string> = {
    'arcane-runes': `
      <circle cx="50" cy="50" r="42" fill="none" stroke="${color}" stroke-width="2"/>
      <circle cx="50" cy="50" r="32" fill="none" stroke="${color}" stroke-width="2"/>
      <polygon points="50,20 62,42 88,42 68,58 76,82 50,68 24,82 32,58 12,42 38,42" fill="none" stroke="${color}" stroke-width="2"/>
      <text x="50" y="12" text-anchor="middle" fill="${color}" font-size="8" font-family="serif">ᚠ</text>
      <text x="88" y="52" text-anchor="middle" fill="${color}" font-size="8" font-family="serif">ᚢ</text>
      <text x="74" y="90" text-anchor="middle" fill="${color}" font-size="8" font-family="serif">ᚦ</text>
      <text x="26" y="90" text-anchor="middle" fill="${color}" font-size="8" font-family="serif">ᚨ</text>
      <text x="12" y="52" text-anchor="middle" fill="${color}" font-size="8" font-family="serif">ᚱ</text>
      <circle cx="50" cy="50" r="6" fill="none" stroke="${color}" stroke-width="2"/>
      <circle cx="50" cy="50" r="2" fill="${color}"/>
    `,
  };

  return `${svgStart}${logos[type]}${svgEnd}`;
}

/**
 * Gets the SVG markup for a logo, either from external file or inline.
 */
async function getLogoSvg(type: CardBackLogoType, color: string): Promise<string> {
  const externalPath = externalLogoFiles[type];
  
  if (externalPath) {
    const svgContent = await fetchSvgContent(externalPath);
    return recolorSvg(svgContent, color);
  }
  
  // Inline logo (arcane-runes only)
  return getInlineLogoSvg(type as 'arcane-runes', color);
}

/**
 * Renders an SVG string to a canvas and returns the data URL.
 * Uses a 2x scale for better print quality.
 */
async function renderSvgToDataUrl(svgString: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      // 2x scale for print quality (192px SVG -> 384px canvas)
      const canvas = document.createElement('canvas');
      canvas.width = 384;
      canvas.height = 384;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to get canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0, 384, 384);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load SVG'));
    };

    img.src = url;
  });
}

/**
 * Gets a cached logo data URL, or renders and caches it.
 */
async function getCachedLogoDataUrl(logoType: CardBackLogoType, color: string): Promise<string> {
  const cacheKey = `${logoType}-${color}`;
  const cached = logoCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const svgString = await getLogoSvg(logoType, color);
  const dataUrl = await renderSvgToDataUrl(svgString);
  logoCache.set(cacheKey, dataUrl);
  return dataUrl;
}

/**
 * Configuration for card back rendering.
 */
interface CardBackRenderConfig {
  cardWidth: number;      // in inches
  cardHeight: number;     // in inches
  borderWidth: number;    // border width in pixels (at 96dpi)
  borderRadius: number;   // corner radius in pixels (at 96dpi)
  logoSize: number;       // in inches
}

const DEFAULT_CONFIG: CardBackRenderConfig = {
  cardWidth: 2.5,
  cardHeight: 3.5,
  borderWidth: 4,         // 4px border
  borderRadius: 6,        // 6px radius to match card fronts
  logoSize: 1,            // 1 inch logo
};

// Cache for rendered card back images: "borderColor-logoType-style" -> data URL
const cardBackImageCache = new Map<string, string>();

/**
 * Renders a complete card back to a canvas and returns it as a data URL.
 * Uses canvas 2D API which properly supports rounded rectangles.
 */
async function renderCardBackToDataUrl(
  borderColor: string,
  logoType: CardBackLogoType,
  style: CardBackStyle,
  config: CardBackRenderConfig
): Promise<string> {
  const cacheKey = `${borderColor}-${logoType}-${style}`;
  const cached = cardBackImageCache.get(cacheKey);
  if (cached) return cached;

  const { borderWidth, borderRadius } = config;
  // Render at 2x for print quality (240px * 2 = 480px width)
  const scale = 2;
  const widthPx = 240 * scale;
  const heightPx = 336 * scale;
  const scaledBorderWidth = borderWidth * scale;
  const scaledBorderRadius = borderRadius * scale;
  const logoSizePx = 96 * scale; // 1 inch at 96dpi, scaled

  const isSolid = style === 'solid';
  const bgColor = isSolid ? borderColor : '#ffffff';
  const logoColor = isSolid ? '#ffffff' : borderColor;

  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');

  // Draw rounded rectangle background using path with arcs (works in all browsers)
  ctx.fillStyle = bgColor;
  ctx.beginPath();
  drawRoundedRectPath(ctx, 0, 0, widthPx, heightPx, scaledBorderRadius);
  ctx.fill();

  // Draw rounded rectangle border
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = scaledBorderWidth;
  ctx.beginPath();
  // Inset the border so it's fully visible
  const inset = scaledBorderWidth / 2;
  drawRoundedRectPath(ctx, inset, inset, widthPx - scaledBorderWidth, heightPx - scaledBorderWidth, scaledBorderRadius);
  ctx.stroke();

  // Draw logo centered
  const logoDataUrl = await getCachedLogoDataUrl(logoType, logoColor);
  const logoImg = await loadImage(logoDataUrl);
  const logoX = (widthPx - logoSizePx) / 2;
  const logoY = (heightPx - logoSizePx) / 2;
  ctx.drawImage(logoImg, logoX, logoY, logoSizePx, logoSizePx);

  const dataUrl = canvas.toDataURL('image/png');
  cardBackImageCache.set(cacheKey, dataUrl);
  return dataUrl;
}

/**
 * Draws a rounded rectangle path manually using arcs.
 * This works in all browsers, unlike ctx.roundRect which may not be available everywhere.
 */
function drawRoundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.arcTo(x + width, y, x + width, y + r, r);
  ctx.lineTo(x + width, y + height - r);
  ctx.arcTo(x + width, y + height, x + width - r, y + height, r);
  ctx.lineTo(x + r, y + height);
  ctx.arcTo(x, y + height, x, y + height - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

/**
 * Loads an image from a data URL.
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Draws a card back onto the PDF by rendering to canvas first.
 * This ensures proper rounded corners since canvas 2D arcTo works reliably.
 *
 * @param pdf - The jsPDF instance
 * @param x - X position in inches
 * @param y - Y position in inches
 * @param borderColor - The border/accent color (hex)
 * @param logoType - Which logo to display
 * @param style - 'solid' (colored background) or 'white' (white background)
 */
export async function drawCardBack(
  pdf: jsPDF,
  x: number,
  y: number,
  borderColor: string,
  logoType: CardBackLogoType,
  style: CardBackStyle,
  config: CardBackRenderConfig = DEFAULT_CONFIG
): Promise<void> {
  const { cardWidth, cardHeight } = config;

  // Render card back to canvas image
  const cardBackDataUrl = await renderCardBackToDataUrl(borderColor, logoType, style, config);

  // Add the image to the PDF
  pdf.addImage(cardBackDataUrl, 'PNG', x, y, cardWidth, cardHeight);
}

/**
 * Pre-renders a logo to cache before PDF generation starts.
 * Call this during the "preparing" phase to warm the cache.
 */
export async function preRenderLogo(logoType: CardBackLogoType, color: string): Promise<void> {
  await getCachedLogoDataUrl(logoType, color);
}

/**
 * Clears the logo cache. Call this if colors change between generations.
 */
export function clearLogoCache(): void {
  logoCache.clear();
  cardBackImageCache.clear();
}
